import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Clock } from "lucide-react";
import { useSubscription } from "@/context/SubscriptionContext";

/**
 * Trial countdown banner — 7-günlük ücretsiz deneme sırasında görünen
 * parlayan üst şerit. Dashboard'un tepesinde sabit, otomatik gizlenir
 * ücretli aboneliğe geçildiğinde.
 *
 * v2 (Şub 2026): "Parlayan" pulse animasyonu + ışıltılı gradient akış.
 */
export default function TrialBanner() {
  const { isTrial, trialDaysLeft, trialEndAt } = useSubscription();
  if (!isTrial) return null;

  const urgent = trialDaysLeft <= 2;
  const critical = trialDaysLeft <= 1;
  const endDate = trialEndAt
    ? new Date(trialEndAt).toLocaleDateString("tr-TR", { day: "numeric", month: "long" })
    : "";

  const accent = critical ? "#ef4444" : urgent ? "#f59e0b" : "var(--gold)";
  const borderCol = critical ? "rgba(239,68,68,0.35)" : urgent ? "rgba(245,158,11,0.35)" : "rgba(212,175,55,0.28)";

  return (
    <div
      className="relative border-b overflow-hidden trial-banner-glow"
      style={{ borderColor: borderCol }}
      data-testid="trial-banner"
    >
      {/* Parlayan gradient akış — arka planda soldan sağa animasyon */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none trial-banner-shine"
        style={{
          background: `linear-gradient(90deg, transparent 0%, ${critical ? "rgba(239,68,68,0.18)" : urgent ? "rgba(245,158,11,0.16)" : "rgba(212,175,55,0.14)"} 45%, transparent 100%)`,
        }}
      />
      <div className="max-w-[1800px] mx-auto pl-14 pr-3 sm:px-6 lg:pl-6 py-1.5 sm:py-2.5 flex items-center justify-between gap-2 sm:gap-3 relative">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {urgent ? (
            <Clock size={12} style={{ color: accent }} className="shrink-0 animate-pulse" />
          ) : (
            <Sparkles size={12} style={{ color: accent }} className="shrink-0 trial-banner-sparkle" />
          )}
          <div
            className="text-[11px] sm:text-sm text-white/90 truncate leading-tight"
            data-testid="trial-countdown"
          >
            <span className="hidden sm:inline">Deneme sürenizin bitmesine </span>
            <span className="sm:hidden">Deneme: </span>
            <span
              className="font-mono font-semibold"
              style={{ color: accent, textShadow: `0 0 8px ${accent}55` }}
            >
              {trialDaysLeft} gün
            </span>
            <span className="hidden sm:inline"> kaldı</span>
            {endDate && (
              <span className="hidden md:inline text-neutral-500 text-xs ml-2">· {endDate}</span>
            )}
          </div>
        </div>
        <Link
          to="/pricing"
          className="shrink-0 inline-flex items-center gap-1 px-2.5 sm:px-4 py-1 sm:py-2 text-[10px] sm:text-[11px] font-mono tracking-wider whitespace-nowrap border transition-colors duration-200 hover:bg-[--gold] hover:text-black trial-banner-cta-glow"
          style={{ borderColor: accent, color: accent }}
          data-testid="trial-upgrade-btn"
        >
          <span className="hidden sm:inline">ABONELİK BAŞLAT</span>
          <span className="sm:hidden">ABONE OL</span>
          <ArrowRight size={11} />
        </Link>
      </div>

      <style>{`
        @keyframes trial-shine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .trial-banner-shine { animation: trial-shine 4.5s ease-in-out infinite; }
        @keyframes trial-sparkle {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.15); }
        }
        .trial-banner-sparkle { animation: trial-sparkle 2s ease-in-out infinite; }
        @keyframes trial-cta-glow {
          0%, 100% { box-shadow: 0 0 0 rgba(212,175,55,0); }
          50% { box-shadow: 0 0 12px rgba(212,175,55,0.35); }
        }
        .trial-banner-cta-glow { animation: trial-cta-glow 3.5s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
